/**
 * useFeedbackSubmit + useGetFeedback hooks
 *
 * Wraps the `feedback-submit` (POST) and `get-feedback` (GET) CF Worker
 * handlers in React Query mutations/queries. Both endpoints validate their
 * wire shapes end-to-end via `@dramadetector/shared/schemas/feedback`
 * (`FeedbackSubmitResultDto`, `GetFeedbackResultDto`) using
 * `fetchJsonValidated()`. Server-side schema drift surfaces as an immediate
 * runtime error rather than silent UI breakage.
 *
 * Usage:
 *   - `useFeedbackSubmit()` → mutation hook; callers `mutateAsync({
 *     analysis_id, feedback_type, feedback_source, comment? })`.
 *   - `useGetFeedback(analysisId)` → query hook; returns the caller's
 *     existing feedback for the given analysis (or `null`).
 *
 * Eliminates direct-fetch violations in:
 *   - components/FeedbackButtons.tsx (both analysis-result and shared-link
 *     flows).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FeedbackSubmitResultDto,
  GetFeedbackResultDto,
  type FeedbackSubmitResult,
  type GetFeedbackResult,
  type FeedbackType,
  type AnalysisFeedbackSource,
} from '@dramadetector/shared/schemas';
import { fetchJsonValidated } from '@dramadetector/api-client';
import { getEdgeFunctionUrl } from '../lib/api-url';

export type { FeedbackSubmitResult, GetFeedbackResult, FeedbackType, AnalysisFeedbackSource };

/** Inputs accepted by `useFeedbackSubmit`. */
export interface FeedbackSubmitInput {
  /** Analysis row (analysis_history.id) the feedback is about. */
  analysis_id: string;
  /** Thumbs-up / thumbs-down sentiment. */
  feedback_type: FeedbackType;
  /** UI surface the feedback was submitted from. */
  feedback_source: AnalysisFeedbackSource;
  /** Optional free-form comment. */
  comment?: string;
}

async function postFeedback(input: FeedbackSubmitInput): Promise<FeedbackSubmitResult> {
  return fetchJsonValidated(FeedbackSubmitResultDto, getEdgeFunctionUrl('feedback-submit'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

/**
 * React Query mutation hook for `feedback-submit`.
 *
 * Duplicates within the 1-hour Redis dedup window return HTTP 409; those
 * surface as a thrown `Error` with the server's message so the caller can
 * decide whether to surface "already submitted" UX or ignore silently.
 */
export function useFeedbackSubmit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: FeedbackSubmitInput) => postFeedback(input),
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: ['feedback', input.analysis_id] });
    },
  });
}

/**
 * React Query hook for `get-feedback`. Returns the caller's most-recent
 * feedback for the given analysis, or `null` if none / anonymous.
 *
 * Disabled when `analysisId` is falsy so it's safe to call before the
 * parent analysis has loaded.
 */
export function useGetFeedback(analysisId: string | undefined) {
  return useQuery({
    queryKey: ['feedback', analysisId],
    queryFn: async (): Promise<GetFeedbackResult> => {
      const url = `${getEdgeFunctionUrl('get-feedback')}?analysis_id=${encodeURIComponent(
        analysisId as string,
      )}`;
      return fetchJsonValidated(GetFeedbackResultDto, url);
    },
    enabled: !!analysisId,
    staleTime: 1000 * 60, // 1 minute — feedback rarely changes after submit
    retry: false, // 404 (analysis not owned) is expected; don't retry
  });
}
